import React from 'react';
import { Coins, Sparkles, CreditCard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const BillingTab = ({
  displayUser, maxCredits, creditPercent, paymentMethod, setPaymentMethod,
  paypalConfigured, creditPackages, buyingCredits, handleBuyCredits,
  customCredits, setCustomCredits, customBuying, handleCustomCredits, navigate
}) => {
  const inputClass = "bg-background border-input text-white placeholder:text-muted-foreground focus:border-blue-500/50";

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground">Current Plan</CardTitle>
          <CardDescription className="text-muted-foreground">Manage your subscription</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-6 bg-blue-600/10 rounded-xl border-2 border-blue-500/30">
            <div>
              <h3 className="text-2xl font-bold text-white" data-testid="billing-plan">{displayUser.plan} Plan</h3>
              <p className="text-blue-300 mt-1">Access to all AI models with Universal Key</p>
            </div>
            <Button variant="outline" className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10" onClick={() => navigate('/#pricing')}>Upgrade Plan</Button>
          </div>
          <div className="mt-6 p-4 bg-muted rounded-xl border border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Coins className="w-5 h-5 text-amber-500" /><span className="text-foreground font-medium">Credit Usage</span></div>
              <span className="text-foreground font-bold" data-testid="billing-credits">{displayUser.credits ?? 0} / {maxCredits}</span>
            </div>
            <div className="w-full h-3 bg-accent rounded-full overflow-hidden">
              <div data-testid="billing-credit-bar" className={`h-full rounded-full transition-all duration-500 ${creditPercent > 50 ? 'bg-emerald-500' : creditPercent > 20 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${creditPercent}%` }} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-400" />Buy More Credits</CardTitle>
          <CardDescription className="text-muted-foreground">Purchase additional credit packs via Stripe or PayPal</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-6" data-testid="payment-method-selector">
            <button onClick={() => setPaymentMethod('stripe')} data-testid="select-stripe"
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${paymentMethod === 'stripe' ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-border text-muted-foreground hover:border-blue-500/30'}`}>
              <CreditCard className="w-5 h-5" /><span className="font-medium text-sm">Stripe</span>
            </button>
            <button onClick={() => setPaymentMethod('paypal')} data-testid="select-paypal"
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-border text-muted-foreground hover:border-blue-500/30'}`}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
              <span className="font-medium text-sm">PayPal{!paypalConfigured && <span className="text-xs text-muted-foreground ml-1">(Setup needed)</span>}</span>
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="buy-credits-grid">
            {creditPackages.map((pkg) => (
              <div key={pkg.id} data-testid={`settings-credit-pkg-${pkg.id}`} className="border-2 border-border bg-muted rounded-2xl p-5 text-center hover:border-blue-500/40 transition-all cursor-pointer group" onClick={() => handleBuyCredits(pkg.id, pkg.credits)}>
                <div className="text-lg font-bold text-foreground">{pkg.credits.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground mb-2">credits</div>
                <div className="text-2xl font-bold text-blue-400 mb-1">{pkg.price}</div>
                <div className="text-xs text-muted-foreground">{pkg.per}</div>
                {pkg.badge && <span className="inline-block mt-2 bg-emerald-500/20 text-emerald-400 text-xs font-semibold px-2 py-0.5 rounded-full">{pkg.badge}</span>}
                <Button variant="outline" size="sm" disabled={buyingCredits === pkg.id} className="mt-3 w-full border-input text-foreground/80 hover:bg-blue-500/10 hover:border-blue-500/40">{buyingCredits === pkg.id ? 'Processing...' : `Pay with ${paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}`}</Button>
              </div>
            ))}
          </div>

          <div className="mt-6 p-5 bg-muted rounded-2xl border-2 border-dashed border-input" data-testid="custom-credits-section">
            <h4 className="text-sm font-semibold text-foreground mb-1">Custom Amount</h4>
            <p className="text-xs text-muted-foreground mb-3">Enter any amount (min 10 credits) &middot; $0.20 per credit</p>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor="custom-credits" className="text-muted-foreground text-xs">Credits</Label>
                <Input id="custom-credits" data-testid="custom-credits-input" type="number" min="10" step="10" placeholder="e.g. 250"
                  value={customCredits} onChange={(e) => setCustomCredits(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomCredits()} className={`mt-1 ${inputClass}`} />
              </div>
              <div className="text-center pb-2 min-w-[80px]">
                <div className="text-lg font-bold text-blue-400">${customCredits ? (parseInt(customCredits) * 0.20).toFixed(2) : '0.00'}</div>
                <div className="text-xs text-muted-foreground">total</div>
              </div>
              <Button data-testid="buy-custom-credits-btn" onClick={handleCustomCredits}
                disabled={customBuying || !customCredits || parseInt(customCredits) < 10}
                className="bg-blue-600 hover:bg-blue-500 min-w-[140px]">
                {customBuying ? 'Processing...' : `Pay with ${paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}`}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingTab;
